import { useEffect, useMemo, useState } from "react";
import { onValue, ref, set } from "firebase/database";
import { db } from "../firebaseConfig";
import { useAuthStore } from "../stores/useAuthStore";

export const DEFAULT_ALERTS_CATALOG = {
  categories: [
    { id: "fauna", label: "Fauna", icon: "pawprints.svg" },
    { id: "trilho", label: "Trilho", icon: "trail.svg" },
    { id: "seguranca", label: "Segurança", icon: "security.svg" },
    { id: "criticos", label: "Alertas", icon: "alert.svg" },
  ],
  types: {
    fauna: [
      { id: "avistado", label: "Animal avistado", icon: "deer.svg" },
      { id: "ferido", label: "Animal ferido", icon: "warning.svg" },
      { id: "morto", label: "Animal morto", icon: "dead.svg" },
    ],
    trilho: [
      { id: "arvore_caida", label: "Árvore caída", icon: "forest.svg" },
      { id: "caminho_bloqueado", label: "Caminho bloqueado", icon: "warning.svg" },
      { id: "estado_trilho", label: "Estado do trilho", icon: "trilho.svg" },
    ],
    seguranca: [
      { id: "perigo_geral", label: "Perigo geral", icon: "warning.svg" },
      { id: "passagem_condicionada", label: "Passagem condicionada", icon: "alert.svg" },
      { id: "passagem_tecnica", label: "Passagem técnica", icon: "climb.svg" },
      { id: "vento_forte", label: "Vento forte", icon: "wind.svg" },
      { id: "calor_extremo", label: "Calor extremo", icon: "fire.svg" },
      { id: "gelo_neve", label: "Gelo / neve", icon: "snow.svg" },
    ],
    criticos: [
      { id: "incendio", label: "Incêndio / fumo", icon: "fire.svg" },
      { id: "autoridades", label: "Autoridades no trilho", icon: "security.svg" },
      { id: "caes", label: "Cães agressivos", icon: "wolf.svg" },
      { id: "trilho_fechado", label: "Trilho fechado", icon: "alert.svg" },
      { id: "novo_utilizador", label: "Novo utilizador", icon: "adduser.svg" },
      { id: "nova_inscricao", label: "Nova inscrição", icon: "new-event-notification.svg" },
      { id: "pagamento_recebido", label: "Pagamento recebido", icon: "mbway.svg" },
      { id: "pagamento_subscricao", label: "Pagamento de subscrição", icon: "cup-trophy.svg" },
      { id: "pedido_guia_online", label: "Pedido de guia online", icon: "guide.svg" },
      { id: "pedido_trilho_personalizado", label: "Pedido de trilho personalizado", icon: "create-trail.svg" },
      { id: "app_issue_report", label: "Report de problema", icon: "report.svg" },
    ],
  },
  faunaAnimals: [
    { id: "veado", label: "Veado", icon: "deer.svg" },
    { id: "corco", label: "Corço", icon: "deer.svg" },
    { id: "javali", label: "Javali", icon: "boar.svg" },
    { id: "garrano", label: "Garrano", icon: "horse.svg" },
    { id: "vaca", label: "Vaca", icon: "cow.svg" },
    { id: "cabra", label: "Cabra", icon: "goat.svg" },
    { id: "raposa", label: "Raposa", icon: "fox.svg" },
    { id: "lobo", label: "Lobo", icon: "wolf.svg" },
    { id: "cobra", label: "Cobra", icon: "snake.svg" },
    { id: "reptil", label: "Réptil", icon: "reptile.svg" },
    { id: "outro", label: "Outro", icon: "plus.svg" },
  ],
  faunaCauses: ["Atropelamento", "Armadilha", "Queda", "Ataque", "Doença", "Desconhecida"],
  detailOptions: {
    trilho: {
      arvore_caida: ["Obstáculo parcial", "Obstáculo total"],
      caminho_bloqueado: ["Derrocada", "Pedras soltas", "Deslizamento"],
      estado_trilho: [
        "Lama",
        "Piso escorregadio",
        "Neve",
        "Zona perigosa após chuva",
        "Trilho alagado",
        "Ribeiro cheio",
        "Passagem submersa",
        "Passagem difícil",
        "Trilho pouco visível",
        "Trilho perdido",
        "Marcações apagadas",
        "Vegetação alta",
        "Cortes florestais",
      ],
    },
    seguranca: {
      perigo_geral: ["Zona exposta", "Falésia", "Risco de queda"],
      passagem_tecnica: ["Necessita mãos", "Necessita corda", "Exige experiência"],
      vento_forte: ["Cume", "Crista", "Passadiço"],
      calor_extremo: ["Sem sombra", "Risco de desidratação"],
      gelo_neve: ["Escorregadio", "Risco de hipotermia"],
    },
    criticos: {
      incendio: ["Ativo", "Recente"],
      autoridades: ["GNR", "ICNF", "Fiscalização"],
      caes: ["Sem pastor visível"],
      trilho_fechado: ["Temporário", "Definitivo"],
      novo_utilizador: ["Novo registo"],
      nova_inscricao: ["Evento"],
      pagamento_recebido: ["Evento"],
      pagamento_subscricao: ["Subscrição"],
      pedido_guia_online: ["Novo pedido"],
      pedido_trilho_personalizado: ["Novo pedido"],
      app_issue_report: ["Novo report"],
    },
  },
};

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function sanitizeItems(items) {
  return asArray(items)
    .map((i) => ({
      id: String(i?.id || "").trim(),
      label: String(i?.label || i?.id || "").trim(),
      icon: String(i?.icon || "").trim().replace(/^icons\//, ""),
    }))
    .filter((i) => i.id && i.label);
}

export function buildCatalogFromRaw(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const categories = sanitizeItems(src.categories);
  const categoryIds = new Set(categories.map((c) => c.id));
  const safeCategories = categories.length ? categories : DEFAULT_ALERTS_CATALOG.categories;
  const safeIds = categories.length ? categoryIds : new Set(safeCategories.map((c) => c.id));

  const types = {};
  safeIds.forEach((id) => {
    const sanitized = sanitizeItems(src?.types?.[id]);
    if (id === "criticos" && sanitized.length) {
      const knownIds = new Set(sanitized.map((item) => item.id));
      const missingBuiltIns = asArray(DEFAULT_ALERTS_CATALOG.types.criticos).filter(
        (item) => !knownIds.has(item.id)
      );
      types[id] = [...sanitized, ...missingBuiltIns];
    } else {
      types[id] = sanitized.length ? sanitized : asArray(DEFAULT_ALERTS_CATALOG.types[id]);
    }
  });

  const faunaAnimals = sanitizeItems(src.faunaAnimals);
  const safeFaunaAnimals = faunaAnimals.length
    ? faunaAnimals
    : DEFAULT_ALERTS_CATALOG.faunaAnimals;

  const faunaCauses = asArray(src.faunaCauses)
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  const detailOptions = {};
  const defaultDetails = DEFAULT_ALERTS_CATALOG.detailOptions || {};

  const allCats = new Set([
    ...Object.keys(defaultDetails),
    ...Object.keys(src?.detailOptions || {}),
    ...Array.from(safeIds),
  ]);

  allCats.forEach((cat) => {
    detailOptions[cat] = {};
    const defaultByType = defaultDetails?.[cat] || {};
    const srcByType = src?.detailOptions?.[cat] || {};
    const typeIds = new Set([
      ...Object.keys(defaultByType),
      ...Object.keys(srcByType),
      ...(types?.[cat] || []).map((t) => t.id),
    ]);

    typeIds.forEach((typeId) => {
      const srcList = asArray(srcByType?.[typeId])
        .map((v) => String(v || "").trim())
        .filter(Boolean);
      if (srcList.length) {
        detailOptions[cat][typeId] = srcList;
        return;
      }
      const fallbackList = asArray(defaultByType?.[typeId])
        .map((v) => String(v || "").trim())
        .filter(Boolean);
      detailOptions[cat][typeId] = fallbackList;
    });
  });

  return {
    categories: safeCategories,
    types,
    faunaAnimals: safeFaunaAnimals,
    faunaCauses: faunaCauses.length ? faunaCauses : DEFAULT_ALERTS_CATALOG.faunaCauses,
    detailOptions,
  };
}

export function useAlertsCatalog() {
  const [catalog, setCatalog] = useState(DEFAULT_ALERTS_CATALOG);
  const [loading, setLoading] = useState(true);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    const r = ref(db, "config/alertsCatalog");
    const unsub = onValue(
      r,
      (snap) => {
        const raw = snap.val();
        setCatalog(buildCatalogFromRaw(raw));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  const saveCatalog = useMemo(
    () => async (nextCatalog) => {
      const payload = buildCatalogFromRaw(nextCatalog);
      await set(ref(db, "config/alertsCatalog"), {
        ...payload,
        updatedAt: Date.now(),
        updatedBy: user?.uid || null,
      });
    },
    [user?.uid]
  );

  return { catalog, loading, saveCatalog };
}
